import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALLOWED_POLICY_CONTENT_TYPES,
  isAllowedPolicyContentType,
  MAX_POLICY_BYTES,
  type PolicyApproval,
  type PolicyAuditSummary,
  type PolicyIngestion,
  type PolicySummary,
} from '@besideu/shared/policies';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { useSession } from '@/lib/auth.ts';
import { formatBytes, formatDate } from '@/lib/format.ts';

export function HrPolicies() {
  const session = useSession('hr-shell');
  const canUpload = session.role === 'HR_Admin';

  const queryClient = useQueryClient();
  const policies = useQuery({
    queryKey: ['policies'],
    queryFn: () => api.get<{ policies: PolicySummary[] }>('/api/hr/policies'),
    // Slice 16 — while any row is mid-ingest, poll so the status pill flips
    // Pending → Processing → Ready without the HR user having to refresh.
    // Polling stops once every row is in a terminal state.
    refetchInterval: (query) => {
      const data = query.state.data as { policies?: PolicySummary[] } | undefined;
      // Slice 19 — poll while either the RAG ingest OR the multi-agent audit
      // is mid-flight so HR sees both pills flip without a manual refresh.
      // Polling stops once every row is in a terminal state on both axes.
      const inFlight = data?.policies?.some(
        (p) =>
          p.ingestion?.status === 'Pending' ||
          p.ingestion?.status === 'Processing' ||
          p.audit?.status === 'Pending' ||
          p.audit?.status === 'Processing',
      );
      return inFlight ? 3000 : false;
    },
  });

  const upload = useMutation({
    mutationFn: (form: FormData) =>
      api.postForm<{ policy: PolicySummary }>('/api/hr/policies', form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] }),
  });

  const remove = useMutation({
    mutationFn: (policyId: string) => api.del<{ ok: true }>(`/api/hr/policies/${policyId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] }),
  });

  // Approval gate (spec §4) — flips a Draft policy to Approved so Maeve may
  // cite it. HR_Admin only; the button is hidden for HR_Viewers (and the
  // server enforces `requireRole('HR_Admin')` regardless).
  // PROJECT §4 Step 2 — the server returns 409 `rules_review_incomplete`
  // when the rule review is still open. Surface the server's copy so HR
  // sees the canonical reason (e.g. "5 rules pending review") rather
  // than a generic "Could not approve."
  const approve = useMutation({
    mutationFn: (policyId: string) =>
      api.post<{ policy: PolicySummary }>(`/api/hr/policies/${policyId}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] }),
  });

  return (
    <section className="space-y-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Policies</h1>
        <p className="mt-2 text-muted-foreground">
          Upload your bereavement leave policy and any related guidance. These documents are stored
          in EU R2 and indexed, but Maeve only cites a policy once an HR Admin has reviewed it and
          marked it <span className="font-medium">Approved</span> — until then it stays a draft and
          Maeve won&rsquo;t quote it.
        </p>
      </header>

      {canUpload ? (
        <UploadForm
          pending={upload.isPending}
          error={errorMessage(upload.error)}
          onSubmit={(form) => upload.mutate(form)}
        />
      ) : (
        <p className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          HR Viewers can browse and download policies. Ask an HR Admin to upload new documents.
        </p>
      )}

      <PoliciesList
        loading={policies.isPending}
        error={policies.error ? 'Could not load policies.' : null}
        policies={policies.data?.policies ?? []}
        canManage={canUpload}
        onDelete={(id) => remove.mutate(id)}
        deletingId={remove.isPending ? remove.variables : null}
        onApprove={(id) => approve.mutate(id)}
        approvingId={approve.isPending ? approve.variables : null}
        approveError={approveErrorMessage(approve.error)}
        approveErrorId={approve.isError ? (approve.variables ?? null) : null}
      />
    </section>
  );
}

function UploadForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (form: FormData) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [clientError, setClientError] = useState<string | null>(null);

  function handle(e: FormEvent) {
    e.preventDefault();
    const file = inputRef.current?.files?.[0];
    if (!file) {
      setClientError('Choose a file first.');
      return;
    }
    if (file.size === 0) {
      setClientError('That file is empty.');
      return;
    }
    if (file.size > MAX_POLICY_BYTES) {
      setClientError('That file is over the 10 MB limit.');
      return;
    }
    if (!isAllowedPolicyContentType(file.type)) {
      setClientError('Only PDF, Word, plain text, or markdown files are accepted.');
      return;
    }
    setClientError(null);
    const form = new FormData();
    form.set('file', file);
    onSubmit(form);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <form
      onSubmit={handle}
      className="flex flex-col gap-3 rounded-lg border border-border p-6 sm:flex-row sm:items-end"
    >
      <label className="flex-1">
        <span className="mb-1 block text-sm font-medium">Policy document</span>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_POLICY_CONTENT_TYPES.join(',')}
          // 16px text avoids iOS Safari zoom-on-focus — same baseline as Field.tsx.
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base file:mr-4 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-2 file:text-sm file:font-medium"
        />
        <span className="mt-1 block text-xs text-muted-foreground">
          PDF, Word, plain text, or markdown — 10 MB max.
        </span>
      </label>
      <div className="flex flex-col">
        <Button type="submit" loading={pending}>
          Upload
        </Button>
        {(clientError ?? error) ? (
          <p className="mt-1 text-sm text-traffic-red" role="alert">
            {clientError ?? error}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function PoliciesList({
  loading,
  error,
  policies,
  canManage,
  onDelete,
  deletingId,
  onApprove,
  approvingId,
  approveError,
  approveErrorId,
}: {
  loading: boolean;
  error: string | null;
  policies: PolicySummary[];
  canManage: boolean;
  onDelete: (policyId: string) => void;
  deletingId: string | null;
  onApprove: (policyId: string) => void;
  approvingId: string | null;
  approveError: string | null;
  approveErrorId: string | null;
}) {
  if (loading) return <p className="text-muted-foreground">Loading policies…</p>;
  if (error) return <p className="text-traffic-red">{error}</p>;
  if (policies.length === 0) {
    return <p className="text-muted-foreground">No policies uploaded yet.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">File</th>
            <th className="px-4 py-2 font-medium">Maeve use</th>
            <th className="px-4 py-2 font-medium">Indexed</th>
            <th className="px-4 py-2 font-medium">Audit</th>
            <th className="px-4 py-2 font-medium">Key rules</th>
            <th className="px-4 py-2 font-medium">Size</th>
            <th className="px-4 py-2 font-medium">Uploaded</th>
            <th className="px-4 py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => (
            <tr key={p.id} className="border-t border-border">
              <td className="px-4 py-2">{p.filename}</td>
              <td className="px-4 py-2">
                <ApprovalPill approval={p.approval} />
              </td>
              <td className="px-4 py-2">
                <IngestionPill ingestion={p.ingestion} />
              </td>
              <td className="px-4 py-2">
                <AuditPill audit={p.audit} policyId={p.id} />
              </td>
              <td className="px-4 py-2">
                <RulesPill rules={p.rules} policyId={p.id} />
              </td>
              <td className="px-4 py-2 text-muted-foreground">{formatBytes(p.sizeBytes)}</td>
              <td className="px-4 py-2 text-muted-foreground">{formatDate(p.uploadedAt)}</td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                <a
                  className="inline-flex min-h-[44px] items-center text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
                  href={`/api/hr/policies/${p.id}`}
                  target="_blank"
                  rel="noopener"
                  aria-label={`Download ${p.filename}`}
                >
                  Download
                </a>
                {canManage && p.approval.status === 'Draft' ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        confirm(
                          `Approve "${p.filename}"? Maeve will start citing this policy to employees and managers.`,
                        )
                      ) {
                        onApprove(p.id);
                      }
                    }}
                    // PROJECT §4 Step 2 — refuse approve from the list when
                    // the rule review is still incomplete. Server enforces
                    // 409 too, but blocking in the UI gives HR a clear
                    // "review the rules first" affordance.
                    //
                    // `p.rules?.blocksPolicyApproval ?? false`: `rules` is
                    // null for pre-Step-2 policies (the `rules` summary
                    // is null on `PolicySummary` when no extraction
                    // state row exists). The `?? false` defaults to
                    // unblocked for that case — preserving the
                    // pre-Step-2 behaviour where approve is allowed.
                    disabled={approvingId === p.id || (p.rules?.blocksPolicyApproval ?? false)}
                    title={
                      p.rules?.blocksPolicyApproval
                        ? 'Complete the key-rules review before approving.'
                        : undefined
                    }
                    className="ml-3 inline-flex min-h-[44px] items-center text-traffic-green-text underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none disabled:opacity-50"
                  >
                    {approvingId === p.id ? 'Approving…' : 'Approve'}
                  </button>
                ) : null}
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        confirm(`Delete "${p.filename}"? This removes the document and its index.`)
                      ) {
                        onDelete(p.id);
                      }
                    }}
                    disabled={deletingId === p.id}
                    className="ml-3 inline-flex min-h-[44px] items-center text-traffic-red underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none disabled:opacity-50"
                  >
                    {deletingId === p.id ? 'Deleting…' : 'Delete'}
                  </button>
                ) : null}
                {approveError && approveErrorId === p.id ? (
                  <p className="mt-1 text-xs text-traffic-red" role="alert">
                    {approveError}
                  </p>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Approval gate (spec §4) — whether Maeve may cite this policy yet. `Draft`
// is amber (action needed: an HR Admin must review + approve); `Approved` is
// green (Maeve will cite it). The pill is the at-a-glance "is this live for
// Maeve?" signal that complements the Approve action in the row.
function ApprovalPill({ approval }: { approval: PolicyApproval }) {
  const approved = approval.status === 'Approved';
  const cls = approved
    ? 'bg-traffic-green/20 text-traffic-green-text'
    : 'bg-traffic-amber/20 text-traffic-amber-text';
  const title = approved
    ? approval.approvedAt
      ? `Approved ${formatDate(approval.approvedAt)} — Maeve cites this policy`
      : 'Approved — Maeve cites this policy'
    : 'Draft — review and approve before Maeve will cite it';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}
      title={title}
    >
      {approved ? 'Approved' : 'Draft'}
    </span>
  );
}

// Slice 16 — single-source-of-truth pill renderer. Operator-readable copy
// per status (DESIGN §5.2.1). `null` ingestion happens for pre-Slice-16
// uploads that haven't been backfilled — rendered neutral, NOT as an error.
function IngestionPill({ ingestion }: { ingestion: PolicyIngestion | null }) {
  if (!ingestion) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Not indexed
      </span>
    );
  }
  const { label, tone } = describeIngestion(ingestion);
  const cls =
    tone === 'green'
      ? 'bg-traffic-green/20 text-traffic-green-text'
      : tone === 'amber'
        ? 'bg-traffic-amber/20 text-traffic-amber-text'
        : tone === 'red'
          ? 'bg-traffic-red/20 text-traffic-red'
          : 'bg-muted text-muted-foreground';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}
      title={ingestion.lastError ?? undefined}
    >
      {label}
    </span>
  );
}

// Slice 19 — Multi-Agent Policy Audit pill. Mirrors `IngestionPill` but
// wraps Ready audits in a `<Link>` to the detail view. The detail-view link
// is only on Ready because Failed / Unavailable / in-flight states don't
// have a synthesised report to show — clicking would land on a page with
// only status copy.
function AuditPill({ audit, policyId }: { audit: PolicyAuditSummary | null; policyId: string }) {
  if (!audit) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Not audited
      </span>
    );
  }
  const { label, tone } = describeAudit(audit);
  const cls =
    tone === 'green'
      ? 'bg-traffic-green/20 text-traffic-green-text'
      : tone === 'amber'
        ? 'bg-traffic-amber/20 text-traffic-amber-text'
        : tone === 'red'
          ? 'bg-traffic-red/20 text-traffic-red'
          : 'bg-muted text-muted-foreground';
  const pill = (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}
      title={audit.lastError ?? undefined}
    >
      {label}
    </span>
  );
  if (audit.status === 'Ready' || audit.status === 'Failed') {
    return (
      <Link to={`/hr/policies/${policyId}/audit`} className="hover:underline">
        {pill}
      </Link>
    );
  }
  return pill;
}

// PROJECT §4 Step 2 — rules-review pill. Mirrors `AuditPill` shape so the
// list-view columns are visually consistent. Renders a per-policy status
// (Pending / Processing / Ready / Failed / Unavailable / NoRules) and the
// "X/Y approved" counts so HR can see at a glance which policies still
// need review. Links to the per-policy review page when there's
// something to act on (Ready with pending rules, or Failed).
function RulesPill({ rules, policyId }: { rules: PolicySummary['rules']; policyId: string }) {
  if (!rules) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Not extracted
      </span>
    );
  }
  const { label, tone } = describeRules(rules);
  const cls =
    tone === 'green'
      ? 'bg-traffic-green/20 text-traffic-green-text'
      : tone === 'amber'
        ? 'bg-traffic-amber/20 text-traffic-amber-text'
        : tone === 'red'
          ? 'bg-traffic-red/20 text-traffic-red'
          : 'bg-muted text-muted-foreground';
  const pill = (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}>
      {label}
    </span>
  );
  // Link to the review page when there's something to act on (pending
  // rules, or extraction in a non-happy state). `NoRules` also links
  // because HR may want to confirm "the system said no rules were
  // extractable" before approving the umbrella policy.
  if (
    rules.extractionStatus === 'Ready' ||
    rules.extractionStatus === 'Failed' ||
    rules.extractionStatus === 'Unavailable' ||
    rules.extractionStatus === 'NoRules' ||
    rules.pendingReview > 0
  ) {
    return (
      <Link to={`/hr/policies/${policyId}/extraction`} className="hover:underline">
        {pill}
      </Link>
    );
  }
  return pill;
}

function describeRules(r: NonNullable<PolicySummary['rules']>): {
  label: string;
  tone: 'green' | 'amber' | 'red' | 'neutral';
} {
  if (r.extractionStatus === 'Pending') return { label: 'Rules: Pending…', tone: 'neutral' };
  if (r.extractionStatus === 'Processing') return { label: 'Rules: Processing…', tone: 'neutral' };
  if (r.extractionStatus === 'Unavailable') return { label: 'Rules: Awaiting CGC', tone: 'amber' };
  if (r.extractionStatus === 'Failed') return { label: 'Rules: Failed', tone: 'red' };
  if (r.extractionStatus === 'NoRules') return { label: 'Rules: none', tone: 'neutral' };
  // Ready
  if (r.total === 0) return { label: 'Rules: none', tone: 'neutral' };
  if (r.pendingReview > 0) {
    return {
      label: `Rules: ${r.approved}/${r.total} approved · ${r.pendingReview} to review`,
      tone: 'amber',
    };
  }
  return {
    label: `Rules: ${r.approved}/${r.total} approved`,
    tone: 'green',
  };
}

function describeAudit(a: PolicyAuditSummary): {
  label: string;
  tone: 'green' | 'amber' | 'red' | 'neutral';
} {
  switch (a.status) {
    case 'Pending':
      return { label: 'Pending…', tone: 'neutral' };
    case 'Processing':
      return { label: 'Processing…', tone: 'neutral' };
    case 'Ready': {
      const c = a.overallComplianceScore;
      const e = a.overallEmpathyScore;
      const score = c != null && e != null ? ` (C${c}/E${e})` : '';
      return { label: `Ready${score}`, tone: 'green' };
    }
    case 'Failed':
      return { label: 'Failed', tone: 'red' };
    case 'Unavailable':
      // Governance gate — Policy_Audit_* prompts not yet signed off by
      // legal counsel. Amber, not red — the policy is fine and indexed;
      // the audit feature just isn't enabled yet.
      return { label: 'Awaiting sign-off', tone: 'amber' };
  }
}

function describeIngestion(i: PolicyIngestion): {
  label: string;
  tone: 'green' | 'amber' | 'red' | 'neutral';
} {
  switch (i.status) {
    case 'Pending':
      return { label: 'Pending…', tone: 'neutral' };
    case 'Processing':
      return { label: 'Processing…', tone: 'neutral' };
    case 'Ready':
      return {
        label: i.chunkCount != null ? `Ready (${i.chunkCount} chunks)` : 'Ready',
        tone: 'green',
      };
    case 'Failed':
      // The error code is in `lastError` and rendered as a tooltip on the
      // pill (operator-actionable, not user-friendly). The cell stays short
      // so the table doesn't blow out horizontally.
      return { label: 'Failed', tone: 'red' };
    case 'Skipped':
      // Skipped = legitimate non-RAG-able (e.g. legacy .doc, image-only PDF).
      // Amber, not red — the document is still downloadable, just not
      // searchable.
      return { label: 'Not searchable', tone: 'amber' };
  }
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 413) return 'File is over the 10 MB limit.';
    if (err.status === 415) return 'That file type isn’t accepted.';
    if (err.status === 403) return 'You don’t have permission to upload policies.';
    if (err.status === 400) return 'Could not read that file.';
    return 'Something went wrong uploading the file.';
  }
  return 'Network error.';
}

// PROJECT §4 Step 2 — map an approve failure to HR-facing copy. The button is
// disabled while `blocksPolicyApproval` is true, so the 409 only fires on a
// race (the rule set flipped back to pending — e.g. a re-extraction — between
// list load and click). Surface the server's `pendingReview` count so HR
// knows to revisit the key-rules review rather than seeing a bare failure.
function approveErrorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 409 && err.body?.error === 'rules_review_incomplete') {
      const pending = typeof err.body.pendingReview === 'number' ? err.body.pendingReview : null;
      return pending != null
        ? `${pending} key ${pending === 1 ? 'rule' : 'rules'} still need review before this policy can be approved.`
        : 'Finish the key-rules review before approving this policy.';
    }
    if (err.status === 403) return 'You don’t have permission to approve policies.';
    if (err.status === 404) return 'That policy no longer exists.';
    return 'Could not approve this policy.';
  }
  return 'Network error.';
}
